import { Tooltip, TooltipProps } from "@mui/material";
import React, { KeyboardEvent, ReactNode, useState } from "react";

type ConditionalWrapperProps = {
	/** Boolean condition to determine which wrapper to use (true -> wrapper / false -> fallbackWrapper) */
	condition: boolean;
	/** Use whenTrue function when condition is true */
	whenTrue: (children: ReactNode) => ReactNode;
	/** Use whenFalse function when condition is false */
	whenFalse?: (children: ReactNode) => ReactNode;
	/** Content to be wrapped */
	children: ReactNode;
};

export const ConditionalWrapper = ({ condition, whenTrue, whenFalse, children }: ConditionalWrapperProps) => {
	if (condition) return whenTrue(children);

	if (whenFalse) return whenFalse(children);

	return children;
};

type TooltipWrapperProps = Pick<ConditionalWrapperProps, "condition" | "children"> & {
	title: React.ReactNode;
	/**If the children should be wrapped in a span for tooltip.
	 * This is because MUI requires single element and ConditionalWrapper returns ReactNode which could be multiple elements.
	 * Also if children is disabled, should wrap in span, because disabled elements won't receive pointer events
	 * @default true */
	wrapInSpan?: boolean;
	spanClassName?: string;
} & Omit<TooltipProps, "title" | "children">;

export function TooltipWrapper({
	children,
	condition,
	title,
	wrapInSpan = true,
	spanClassName,
	...props
}: TooltipWrapperProps) {
	// MUI closes its own tooltip from a `keydown` listener on `document`, which sits above everything
	// else — so a modal around this trigger saw that same Escape and closed along with the tooltip.
	// Owning the open state lets us close the tooltip from the trigger's own React handler instead:
	// that runs below `document`, so stopPropagation there keeps the key from ever getting out. Closing
	// by blurring the trigger would work too, but inside a modal it hands focus to the focus trap, which
	// drops it on the modal's close button rather than leaving it on the trigger.
	const [tooltipOpen, setTooltipOpen] = useState(false);

	// Only swallow Escape while the tooltip is actually showing, so it still reaches the modal when the
	// trigger merely has focus.
	const handleTriggerKeyDown = (event: KeyboardEvent) => {
		if (event.key !== "Escape" || !tooltipOpen) return;
		event.stopPropagation();
		setTooltipOpen(false);
	};

	return (
		<ConditionalWrapper
			condition={condition}
			whenTrue={(children) => {
				const isSingleElement = React.isValidElement(children);

				// If you do not wrap in span for disabled elements, then tooltip will not show
				const isDisabled = isSingleElement && Boolean((children.props as { disabled?: boolean }).disabled);

				// If they asked not to wrap, but it's not a single element, wrap to prevent MUI crashing.
				const shouldWrap = wrapInSpan || !isSingleElement || isDisabled;

				// The span we add can take the Escape handler directly; without it the handler has to be
				// cloned onto the caller's own element, keeping whatever onKeyDown they already had.
				const child = children as React.ReactElement;
				const trigger = shouldWrap ? (
					<span
						style={{
							display: "inline-block",
							minWidth: 0,
							verticalAlign: "middle",
						}}
						className={spanClassName}
						onKeyDown={handleTriggerKeyDown}
					>
						{children}
					</span>
				) : (
					React.cloneElement(child, {
						onKeyDown: (event: KeyboardEvent) => {
							const { onKeyDown } = child.props as { onKeyDown?: (event: KeyboardEvent) => void };
							onKeyDown?.(event);
							handleTriggerKeyDown(event);
						},
					})
				);

				return (
					// Open state goes before the caller's props so they can still control `open` themselves.
					<Tooltip
						open={tooltipOpen}
						onOpen={() => setTooltipOpen(true)}
						onClose={() => setTooltipOpen(false)}
						{...props}
						title={title}
					>
						{trigger}
					</Tooltip>
				);
			}}
		>
			{children}
		</ConditionalWrapper>
	);
}
